/* LUCRÜ™ — © 2026 LUCRÜ. All rights reserved. */
import { useEffect, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { CheckCircleIcon, LightningIcon, XCircleIcon } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import Logo from "@/components/Logo";

export const BillingSuccess = () => {
  const [search] = useSearchParams();
  const navigate = useNavigate();
  const sessionId = search.get("session_id");
  const [status, setStatus] = useState("checking");
  const [plan, setPlan] = useState(null);

  useEffect(() => {
    if (!sessionId) {
      setStatus("error");
      return;
    }
    let tries = 0;
    const poll = async () => {
      tries++;
      try {
        const r = await api.get(`/billing/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setStatus("paid");
          setPlan(r.data.plan_id);
          return;
        }
      } catch (e) { /* noop */ }
      if (tries < 10) setTimeout(poll, 2000);
      else setStatus("timeout");
    };
    poll();
  }, [sessionId]);

  return (
    <div className="min-h-screen bg-[#05050A] text-white flex items-center justify-center px-6">
      <motion.div className="glass rounded-2xl p-10 max-w-md w-full text-center" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
        <div className="flex justify-center mb-5">
          <Logo size={64} withBackground className="glow-primary" />
        </div>
        {status === "paid" ? (
          <>
            <CheckCircleIcon size={48} weight="fill" className="mx-auto text-[#39FF14] mb-4" />
            <div className="font-display font-black text-2xl tracking-tight mb-2">You're in</div>
            <div className="text-sm text-white/70 mb-6">
              Welcome to LUCRÜ <span className="text-[#39FF14] capitalize">{plan}</span>. Your 30-day free trial is live on every supported platform.
            </div>
            <button onClick={() => navigate("/dashboard")} className="btn-pill btn-primary w-full justify-center" data-testid="success-cta">
              <LightningIcon size={14} weight="fill" /> Open dashboard
            </button>
          </>
        ) : status === "error" ? (
          <>
            <div className="font-display font-black text-xl mb-3">Missing session</div>
            <Link to="/pricing" className="btn-pill btn-ghost">Back to pricing</Link>
          </>
        ) : (
          <>
            <div className="font-display font-black text-xl mb-3">Confirming payment…</div>
            <div className="text-xs text-white/50">This usually takes a few seconds.</div>
          </>
        )}
      </motion.div>
    </div>
  );
};

export const BillingCancel = () => (
  <div className="min-h-screen bg-[#05050A] text-white flex items-center justify-center px-6">
    <motion.div className="glass rounded-2xl p-10 max-w-md w-full text-center" initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}>
      <div className="flex justify-center mb-5">
        <Logo size={64} withBackground />
      </div>
      <XCircleIcon size={48} weight="duotone" className="mx-auto text-white/60 mb-4" />
      <div className="font-display font-black text-xl mb-3">Checkout cancelled</div>
      <div className="text-sm text-white/60 mb-6">No worries — your free trial is still waiting. Pick a plan whenever you're ready.</div>
      <Link to="/pricing" className="btn-pill btn-primary justify-center" data-testid="cancel-back-to-pricing">
        <LightningIcon size={14} weight="fill" /> See plans
      </Link>
    </motion.div>
  </div>
);

export default BillingSuccess;
